import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { paths, REPORT_VIEW_QUERY } from '@/app/router/paths';
import { Button, Card, IconButton, MonoLabel, Skeleton } from '@/design-system';
import { toPublicTenant } from '@/entities/publicReport';
import {
    formatReportAddress,
    isGenerating,
    pdfProblemOf,
    reportApi,
    reportKeys,
    reportPdfFilename,
} from '@/entities/report';
import { PublicReportContent } from '@/features/public-report/components/PublicReportContent';
import { PublicReportLayout } from '@/features/public-report/components/PublicReportLayout';
import { previewCopy } from '@/features/public-report/copy';
import { usePublicReportPreview } from '@/features/public-report/usePublicReportPreview';
import { useSessionStore } from '@/shared/api';
import { ErrorState, useShellTopBar } from '@/shared/ui';

import { offerDownload } from './browser';
import { reportCopy } from './copy';

/**
 * Het rapport over het hele scherm, zoals de koper het ziet
 * (`/keuringen/:orderId/rapport/weergave`).
 *
 * Op het tabblad Rapport staat het documentvlak naast de sectielijst: een
 * werkvlak om na te lezen en bij te stellen. Om te *zien* wat er straks bij de
 * koper op het scherm staat, is dat te klein — en de klantpagina staat daar in
 * een venster met een kolom ernaast. Dit scherm laat niets anders zien dan de
 * pagina zelf: geen kop van de keuring, geen browserchroom, geen statistieken.
 * Een smalle balk leidt terug; op een telefoon vult de pagina het scherm.
 *
 * De pagina komt van `GET /reports/{report}/preview`, hetzelfde antwoord als de
 * koper met zijn link krijgt, maar met de sessie: het telt geen bezoek en vraagt
 * geen toegangscode. Het gaat over de **nieuwste** versie - het concept dat je
 * net las - en niet over de verstuurde versie; daarvoor is het tabblad
 * Klantpagina.
 */
export function ReportViewPage() {
    const { orderId = '' } = useParams<{ orderId: string }>();
    const navigate = useNavigate();
    const shellMenu = useShellTopBar();
    const session = useSessionStore((state) => state.session);
    const [params] = useSearchParams();
    const [notice, setNotice] = useState<string | null>(null);

    const orderReport = useQuery({
        queryKey: reportKeys.forOrder(orderId),
        queryFn: () => reportApi.forOrder(orderId),
        enabled: orderId !== '',
    });

    const report = orderReport.data?.report ?? null;
    const released = orderReport.data?.released ?? null;
    /* `?versie=klant`: de versie die de koper heeft, in plaats van het nieuwste concept. */
    const clientVersion =
        params.get(REPORT_VIEW_QUERY.param) === REPORT_VIEW_QUERY.value && released !== null
            ? released
            : null;
    const readable = report !== null && !isGenerating(report) && report.state !== 'failed';
    const shownUuid = clientVersion?.reportUuid ?? (readable ? (report?.uuid ?? null) : null);
    const shownVersion = clientVersion?.version ?? report?.version ?? 0;
    const page = usePublicReportPreview(shownUuid);

    /** De PDF haalt de sessie op; een gewone link naar de API werkt hier niet. */
    const pdf = useMutation({
        mutationFn: async (current: { uuid: string; orderNumber: string; version: number }) => {
            const { blob } = await reportApi.pdf(current.uuid);

            return { blob, filename: reportPdfFilename(current) };
        },
        onSuccess: ({ blob, filename }) => {
            setNotice(null);
            offerDownload(blob, filename);
        },
        onError: (error) =>
            setNotice(
                pdfProblemOf(error) === 'setup'
                    ? reportCopy.pdfNotConfigured
                    : reportCopy.view.pdfFailed,
            ),
    });

    const copy = reportCopy.view;
    const toReport = () => void navigate(paths.report(orderId));

    return (
        <div className="flex h-full min-h-0 flex-col bg-[var(--surface-board)]">
            {/* Dezelfde smalle balk als de opname: één weg terug, en waar je naar kijkt. */}
            <header className="flex flex-wrap items-center justify-between gap-[18px] gap-y-3 bg-[var(--ink-800)] px-[22px] py-[14px]">
                <div className="flex min-w-0 items-center gap-4">
                    {shellMenu ? (
                        <IconButton
                            variant="onDark"
                            size="md"
                            icon="menu"
                            aria-label={copy.menu}
                            aria-expanded={shellMenu.open}
                            aria-controls={shellMenu.controls}
                            onClick={shellMenu.toggle}
                        />
                    ) : null}
                    <Button
                        variant="onDark"
                        size="md"
                        icon="back"
                        aria-label={copy.back}
                        onClick={toReport}
                    >
                        {copy.backShort}
                    </Button>
                    <div className="min-w-0">
                        <MonoLabel size={10} tone="onDarkMuted" className="tracking-[0.1em]">
                            {report ? copy.eyebrow(shownVersion) : copy.loading}
                        </MonoLabel>
                        <div className="truncate font-[family-name:var(--font-display)] text-[18px] font-semibold tracking-[-0.01em] text-[var(--text-on-dark)]">
                            {report ? formatReportAddress(report.address) : copy.loading}
                        </div>
                    </div>
                </div>

                {notice ? (
                    <p
                        role="alert"
                        className="m-0 max-w-[420px] text-[13px] leading-[1.5] text-[var(--warn-field)]"
                    >
                        {notice}
                    </p>
                ) : null}
            </header>

            {/* Net als de kop van de keuring: het scherm eronder krijgt precies deze hoogte,
                zodat de klantpagina - die `min-h-full` rekent - vanaf hier rekent en zelf scrolt. */}
            <div className="flex min-h-0 flex-1 flex-col">{body()}</div>
        </div>
    );

    function body() {
        if (orderReport.isError) {
            return (
                <div className="p-[22px]">
                    <ErrorState
                        error={orderReport.error}
                        title={copy.errorTitle}
                        onRetry={() => void orderReport.refetch()}
                    />
                </div>
            );
        }

        if (orderReport.data === undefined) {
            return (
                <div className="p-[22px]">
                    <Skeleton height={420} radius={12} label={copy.loading} />
                </div>
            );
        }

        if (report === null) {
            return state(copy.noneTitle, copy.noneBody);
        }

        if (clientVersion === null && isGenerating(report)) {
            return state(copy.generatingTitle, copy.generatingBody);
        }

        if (clientVersion === null && report.state === 'failed') {
            return state(copy.failedTitle, copy.failedBody);
        }

        return (
            <PublicReportLayout
                tenant={page.tenant ?? fallbackTenant(session?.tenant?.name ?? '')}
                language={page.language}
                filling
            >
                <PublicReportContent
                    state={page}
                    headingLevel={2}
                    previewNote={previewCopy.previewNote}
                    onOpenDocument={() => {
                        if (!pdf.isPending && shownUuid !== null) {
                            setNotice(copy.pdfBusy);
                            pdf.mutate({
                                uuid: shownUuid,
                                orderNumber: report.orderNumber,
                                version: shownVersion,
                            });
                        }
                    }}
                />
            </PublicReportLayout>
        );
    }

    function state(title: string, description: string) {
        return (
            <div className="p-[22px]">
                <Card padding="lg" className="max-w-[640px]">
                    <MonoLabel size={10} className="mb-2 block">
                        {copy.loading}
                    </MonoLabel>
                    <h2 className="m-0 mb-2 font-[family-name:var(--font-display)] text-[17px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                        {title}
                    </h2>
                    <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                        {description}
                    </p>
                    <div className="mt-4">
                        <Button variant="primary" size="md" onClick={toReport}>
                            {copy.back}
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }
}

/** Huisstijl uit de sessie, zolang de pagina zelf nog niet binnen is. */
function fallbackTenant(name: string) {
    return toPublicTenant({
        name,
        logo_url: null,
        accent_color: null,
        phone: null,
        opening_hours: null,
        kvk: null,
        memberships: [],
        languages: ['nl'],
    });
}
